/**
 * Notification Service
 * Handles browser notifications, sound alerts, and permission management
 */

class NotificationService {
  private hasPermission: boolean = false;

  constructor() {
    this.checkPermission();
  }

  /**
   * Check if browser supports notifications
   */
  isSupported(): boolean {
    return "Notification" in window;
  }

  /**
   * Check current notification permission
   */
  checkPermission(): boolean {
    if (!this.isSupported()) return false;
    this.hasPermission = Notification.permission === "granted";
    return this.hasPermission;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      console.warn("Browser notifications not supported");
      return false;
    }

    if (Notification.permission === "granted") {
      this.hasPermission = true;
      return true;
    }

    if (Notification.permission === "denied") {
      console.warn("Notification permission denied");
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.hasPermission = permission === "granted";
      return this.hasPermission;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return false;
    }
  }

  /**
   * Show browser notification
   */
  showNotification(
    title: string,
    options: {
      body?: string;
      icon?: string;
      badge?: string;
      tag?: string;
      data?: any;
      requireInteraction?: boolean;
      silent?: boolean;
    } = {}
  ): void {
    if (!this.hasPermission) {
      console.warn("No notification permission");
      return;
    }

    try {
      const notification = new Notification(title, {
        icon: options.icon || "/logo-black.svg",
        badge: options.badge || "/logo-black.svg",
        body: options.body || "",
        tag: options.tag || "default",
        requireInteraction: options.requireInteraction || false,
        silent: options.silent || false,
        data: options.data,
      });

      // Auto-close after 6 seconds
      setTimeout(() => {
        notification.close();
      }, 6000);

      // Click handler - focus window
      notification.onclick = () => {
        window.focus();
        notification.close();

        // If data contains conversationId, navigate to it
        if (options.data?.conversationId) {
          // You can emit a custom event to navigate
          window.dispatchEvent(
            new CustomEvent("notification-click", {
              detail: { conversationId: options.data.conversationId },
            })
          );
        }
      };
    } catch (error) {
      console.error("Error showing notification:", error);
    }
  }

  /**
   * Play notification sound - Pleasant chime (two-tone)
   */
  playSound(): void {
    try {
      // Generate a pleasant two-tone notification chime using Web Audio API
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();

      // First tone (higher pitch)
      const oscillator1 = audioContext.createOscillator();
      const gainNode1 = audioContext.createGain();
      oscillator1.connect(gainNode1);
      gainNode1.connect(audioContext.destination);

      oscillator1.type = "sine";
      oscillator1.frequency.value = 600; // E5 note
      gainNode1.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode1.gain.linearRampToValueAtTime(
        0.15,
        audioContext.currentTime + 0.01
      );
      gainNode1.gain.exponentialRampToValueAtTime(
        0.001,
        audioContext.currentTime + 0.15
      );

      // Second tone (lower pitch) - plays slightly after first
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();
      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);

      oscillator2.type = "sine";
      oscillator2.frequency.value = 450; // A4 note
      gainNode2.gain.setValueAtTime(0, audioContext.currentTime + 0.08);
      gainNode2.gain.linearRampToValueAtTime(
        0.15,
        audioContext.currentTime + 0.09
      );
      gainNode2.gain.exponentialRampToValueAtTime(
        0.001,
        audioContext.currentTime + 0.25
      );

      // Play both tones
      oscillator1.start(audioContext.currentTime);
      oscillator1.stop(audioContext.currentTime + 0.15);
      oscillator2.start(audioContext.currentTime + 0.08);
      oscillator2.stop(audioContext.currentTime + 0.25);

      // Cleanup
      setTimeout(() => {
        oscillator1.disconnect();
        gainNode1.disconnect();
        oscillator2.disconnect();
        gainNode2.disconnect();
        audioContext.close();
      }, 400);
    } catch (error) {
      console.error("Error playing notification sound:", error);
    }
  }

  /**
   * Play sound and show notification
   */
  notify(
    title: string,
    options: {
      body?: string;
      playSound?: boolean;
      showNotification?: boolean;
      icon?: string;
      tag?: string;
      data?: any;
    } = {}
  ): void {
    const {
      playSound = true,
      showNotification = true,
      ...notificationOptions
    } = options;

    // Play sound if enabled
    if (playSound) {
      this.playSound();
    }

    // Show notification if enabled and tab is not active
    if (showNotification && document.hidden && this.hasPermission) {
      this.showNotification(title, notificationOptions);
    }
  }

  /**
   * Check if notifications should be shown based on quiet hours
   */
  shouldNotify(quietHours?: {
    enabled: boolean;
    start: string;
    end: string;
  }): boolean {
    if (!quietHours?.enabled) return true;

    try {
      const now = new Date();
      const currentTime = `${now.getHours().toString().padStart(2, "0")}:${now
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;

      const { start, end } = quietHours;

      // Simple time comparison
      if (start <= end) {
        // Normal range (e.g., 22:00 - 08:00)
        return currentTime < start || currentTime > end;
      } else {
        // Crosses midnight (e.g., 22:00 - 08:00)
        return currentTime < start && currentTime > end;
      }
    } catch (error) {
      console.error("Error checking quiet hours:", error);
      return true; // Default to allowing notifications
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// Request permission when service is first imported (for agents)
if (typeof window !== "undefined") {
  // Auto-request permission after user interaction
  document.addEventListener(
    "click",
    () => {
      notificationService.requestPermission();
    },
    { once: true }
  );
}
